import Service, { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { isBefore } from 'date-fns/isBefore';
import { startOfMonth } from 'date-fns/startOfMonth';
import { routerHelper } from 'uplisting-frontend/utils/router';
import type {
  ICalendarViewport,
  IMonthlyCalendarViewport,
} from 'uplisting-frontend/services/calendar/utils';
import {
  CalendarPart,
  type ICalendarLoadOptions,
} from 'uplisting-frontend/services/repositories/calendar';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export default class CalendarManagerService extends Service {
  @service router!: Services['router'];

  @service('calendar/data') data!: Services['calendar/data'];
  @service('calendar/utils') utils!: Services['calendar/utils'];

  @service('calendar/interaction')
  interaction!: Services['calendar/interaction'];

  @service('calendar/unit-move')
  unitMove!: Services['calendar/unit-move'];

  @service('calendar/conflict-resolution')
  resolution!: Services['calendar/conflict-resolution'];

  @service('repositories/calendar')
  repository!: Services['repositories/calendar'];

  @tracked normalViewport?: ICalendarViewport;
  @tracked monthlyViewport?: IMonthlyCalendarViewport;

  currentStartDate?: Date;

  public isStaging(booking: ModelRegistry['booking']): boolean {
    const { dragOperation, resizeOperation } = this.interaction;

    return (
      resizeOperation?.record.model === booking ||
      dragOperation?.record.model === booking ||
      this.resolution.isPlaced(booking)
    );
  }

  @cached
  get isOldCalendar(): boolean {
    return (this.router.currentRouteName ?? '').startsWith('xcalendar');
  }

  get viewport(): ICalendarViewport | IMonthlyCalendarViewport {
    if (routerHelper.targetRoute?.includes('monthly')) {
      return this.monthlyViewport as IMonthlyCalendarViewport;
    }

    return this.normalViewport as ICalendarViewport;
  }

  public handleResize(): void {
    if (!this.currentStartDate) {
      return;
    }

    // skip when the grid structure hasn't changed; CSS handles cell-width scaling
    // so only a breakpoint crossing (which changes datesPerPage) requires a rebuild
    if (this.normalViewport?.dates.length === this.utils.datesPerPage) {
      return;
    }

    this.normalViewport = this.utils.createCalendarViewport(
      this.currentStartDate,
    );

    this.data.setupFasterLookup();
  }

  public replaceData(
    startDate: Date,
    payloads: object[],
    length?: number,
  ): void {
    this.data.resetState();

    Object.assign(this, {
      currentStartDate: startDate,
      normalViewport: this.utils.createCalendarViewport(startDate, length),
    });

    this.#push(payloads);
    this.data.setupFasterLookup();
  }

  public addData(payloads: object[], parts: CalendarPart[]): void {
    this.#push(payloads);

    // /calendar's enquiry line reads the store itself
    const changesOccasions = parts.some((part) =>
      part === CalendarPart.enquiries
        ? this.data.cachesEnquiries
        : part !== CalendarPart.rates,
    );

    if (changesOccasions) {
      this.data.setupFasterLookup();

      return;
    }

    if (parts.includes(CalendarPart.rates)) {
      this.data.setupRateLookup();
    }
  }

  public async initialLoadMonthlyData(
    options: ICalendarLoadOptions,
  ): Promise<void> {
    const date = options.startDate;

    const viewport = this.utils.createMonthlyCalendarViewport(date, 4);

    await this.repository.loadData({
      ids: options.ids,
      isMultiUnit: options.isMultiUnit,
      startDate: viewport.startDate,
      endDate: viewport.endDate,
      withTasks: true,
    });

    Object.assign(this, {
      monthlyViewport: viewport,
      currentStartDate: undefined,
    });

    this.data.setupFasterLookup();
  }

  public async loadMonthlyData(options: {
    date: Date;
    ids: string;
    isMultiUnit?: boolean;
  }): Promise<void> {
    const { date, ids, isMultiUnit } = options;

    const isLoadingPrevious = isBefore(
      date,
      (this.monthlyViewport as IMonthlyCalendarViewport).startDate,
    );

    const viewport = this.utils.createMonthlyCalendarViewport(date, 1);

    await this.repository.loadData({
      ids,
      isMultiUnit,
      startDate: viewport.startDate,
      endDate: viewport.endDate,
      withTasks: true,
    });

    this.monthlyViewport = this.utils.addMonthToViewport(
      isLoadingPrevious
        ? viewport
        : (this.monthlyViewport as IMonthlyCalendarViewport),

      isLoadingPrevious
        ? (this.monthlyViewport as IMonthlyCalendarViewport)
        : viewport,
    );

    this.data.setupFasterLookup();
  }

  public rebuildMonthlyViewport(): void {
    const current = this.monthlyViewport;
    const firstMonth =
      current?.monthGroups?.[0]?.month ?? startOfMonth(new Date());
    const numberOfMonths = current?.monthGroups?.length ?? 3;

    const viewport = this.utils.createMonthlyCalendarViewport(
      firstMonth,
      numberOfMonths,
    );

    this.monthlyViewport = viewport;
    this.data.setupFasterLookup();
  }

  #push(payloads: object[]): void {
    for (const payload of payloads) {
      this.repository.push(payload);
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'calendar/manager': CalendarManagerService;
  }
}
